import { useState } from 'react';
import { DayPicker } from 'react-day-picker';
import { format, isValid, parse } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Calendar, Clock, Send, Tag, Wrench, Sparkles } from 'lucide-react';

import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

import type { BroadcastType } from '../types/broadcast.types';

const MESSAGE_TAG_OPTIONS: { value: string; label: string }[] = [
    { value: 'ACCOUNT_UPDATE', label: 'ACCOUNT_UPDATE - Mudanças de conta' },
    { value: 'CONFIRMED_EVENT_UPDATE', label: 'CONFIRMED_EVENT_UPDATE - Eventos confirmados' },
    { value: 'POST_PURCHASE_UPDATE', label: 'POST_PURCHASE_UPDATE - Pós-compra' },
    { value: 'CUSTOMER_FEEDBACK', label: 'CUSTOMER_FEEDBACK - Pesquisas de satisfação' },
    { value: 'HUMAN_AGENT', label: 'HUMAN_AGENT - Atendimento humano' },
];

interface StepScheduleProps {
    isScheduled: boolean;
    scheduledTime: string;
    broadcastType?: BroadcastType;
    messageTag?: string | null;
    onIsScheduledChange: (value: boolean) => void;
    onScheduledTimeChange: (value: string) => void;
    onBroadcastTypeChange?: (type: BroadcastType, tag: string | null) => void;
}


// ──────────────────────────────────────────────────────────
//  Datepicker (popover com DayPicker + input HH:mm)
// ──────────────────────────────────────────────────────────

function DateTimePicker({
    value,
    onChange,
    minDate,
}: {
    value: string;
    onChange: (v: string) => void;
    minDate: Date;
}) {
    const [open, setOpen] = useState(false);

    // value está no formato "yyyy-MM-ddTHH:mm" do datetime-local
    const parsed = value
        ? parse(value, "yyyy-MM-dd'T'HH:mm", new Date())
        : undefined;
    const validDate = parsed && isValid(parsed) ? parsed : undefined;

    const time = validDate ? format(validDate, 'HH:mm') : '';

    const handleDaySelect = (day: Date | undefined) => {
        if (!day) {
            onChange('');
            return;
        }
        // Mantém a hora atual ou usa minDate como referência
        const base = validDate || minDate;
        const merged = new Date(day);
        merged.setHours(base.getHours(), base.getMinutes(), 0, 0);
        onChange(format(merged, "yyyy-MM-dd'T'HH:mm"));
    };

    const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const t = e.target.value; // "HH:mm"
        if (!t || !validDate) return;
        const [h, m] = t.split(':').map(Number);
        const merged = new Date(validDate);
        merged.setHours(h || 0, m || 0, 0, 0);
        onChange(format(merged, "yyyy-MM-dd'T'HH:mm"));
    };

    const display = validDate
        ? format(validDate, "dd 'de' MMM 'de' yyyy', às' HH:mm", { locale: ptBR })
        : 'Selecione data e hora';

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className={cn(
                        'w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl border-2 bg-white text-left transition-colors',
                        validDate
                            ? 'border-[#7dd3fc] text-gray-900'
                            : 'border-gray-200 text-gray-500 hover:border-gray-300'
                    )}
                >
                    <div className="flex items-center gap-3">
                        <Calendar className="w-4 h-4 text-[#7dd3fc]" />
                        <span className="text-sm">{display}</span>
                    </div>
                    <Clock className="w-4 h-4 text-gray-400" />
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
                <div className="p-3">
                    <DayPicker
                        mode="single"
                        locale={ptBR}
                        selected={validDate}
                        onSelect={handleDaySelect}
                        disabled={{ before: minDate }}
                        weekStartsOn={0}
                        showOutsideDays
                        className="rdp-themed"
                    />
                    <div className="border-t border-gray-100 mt-2 pt-3 flex items-center gap-3">
                        <Label htmlFor="time" className="text-xs text-gray-600 flex items-center gap-1.5">
                            <Clock className="w-3.5 h-3.5 text-[#7dd3fc]" />
                            Hora
                        </Label>
                        <Input
                            id="time"
                            type="time"
                            value={time}
                            onChange={handleTimeChange}
                            disabled={!validDate}
                            className="h-9 text-sm w-32"
                        />
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
}


// ──────────────────────────────────────────────────────────
//  Card de tipo de broadcast
// ──────────────────────────────────────────────────────────

function BroadcastTypeCard({
    active,
    onClick,
    icon,
    title,
    description,
}: {
    active: boolean;
    onClick: () => void;
    icon: React.ReactNode;
    title: string;
    description: string;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={cn(
                'p-4 border-2 rounded-xl text-left transition-all',
                active
                    ? 'border-[#7dd3fc] bg-[#7dd3fc]/5'
                    : 'border-gray-200 bg-white hover:border-gray-300'
            )}
        >
            <div className="flex items-center gap-2 mb-1">
                {icon}
                <span className="font-semibold text-gray-900">{title}</span>
            </div>
            <p className="text-xs text-gray-500">{description}</p>
        </button>
    );
}


// ──────────────────────────────────────────────────────────
//  Step principal
// ──────────────────────────────────────────────────────────

export default function StepSchedule({
    isScheduled,
    scheduledTime,
    broadcastType = 'standard',
    messageTag = null,
    onIsScheduledChange,
    onScheduledTimeChange,
    onBroadcastTypeChange,
}: StepScheduleProps) {
    const minDateTime = new Date();
    minDateTime.setMinutes(minDateTime.getMinutes() + 5);

    return (
        <div className="space-y-6">
            <div className="text-center mb-6">
                <h3 className="text-xl font-semibold text-gray-900">Agendamento</h3>
                <p className="text-sm text-gray-600 mt-2">
                    Escolha quando deseja enviar seu broadcast
                </p>
            </div>

            <div className="max-w-md mx-auto space-y-6">
                {/* TIPO DE BROADCAST */}
                {onBroadcastTypeChange && (
                    <div className="space-y-3">
                        <Label className="text-sm font-medium text-gray-700">TIPO DE BROADCAST</Label>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <BroadcastTypeCard
                                active={broadcastType === 'standard'}
                                onClick={() => onBroadcastTypeChange('standard', null)}
                                icon={
                                    <div className={cn(
                                        'w-4 h-4 rounded-full border-2 flex items-center justify-center',
                                        broadcastType === 'standard' ? 'border-[#7dd3fc]' : 'border-gray-300'
                                    )}>
                                        {broadcastType === 'standard' && <div className="w-2 h-2 rounded-full bg-[#7dd3fc]" />}
                                    </div>
                                }
                                title="Standard"
                                description="Dentro da janela de 24h."
                            />

                            <BroadcastTypeCard
                                active={broadcastType === 'utility'}
                                onClick={() => onBroadcastTypeChange('utility', 'ACCOUNT_UPDATE')}
                                icon={
                                    <Wrench className={cn(
                                        'w-4 h-4',
                                        broadcastType === 'utility' ? 'text-[#7dd3fc]' : 'text-gray-400'
                                    )} />
                                }
                                title="Utilidade"
                                description="Fora da janela de 24h."
                            />

                            <BroadcastTypeCard
                                active={broadcastType === 'message_tag'}
                                onClick={() => onBroadcastTypeChange('message_tag', messageTag || 'ACCOUNT_UPDATE')}
                                icon={
                                    <Sparkles className={cn(
                                        'w-4 h-4',
                                        broadcastType === 'message_tag' ? 'text-[#7dd3fc]' : 'text-gray-400'
                                    )} />
                                }
                                title="Custom"
                                description="Escolha a tag manualmente."
                            />
                        </div>

                        {broadcastType === 'message_tag' && (
                            <div className="pt-2">
                                <Label className="text-xs text-gray-500 flex items-center gap-1.5 mb-1.5">
                                    <Tag className="w-3 h-3" />
                                    Categoria da Message Tag
                                </Label>
                                <Select
                                    value={messageTag || 'ACCOUNT_UPDATE'}
                                    onValueChange={(v) => onBroadcastTypeChange('message_tag', v)}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Selecione uma categoria..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {MESSAGE_TAG_OPTIONS.map((opt) => (
                                            <SelectItem key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                    </div>
                )}

                {/* HORÁRIO DE DISPARO */}
                <Label className="text-sm font-medium text-gray-700 block">HORÁRIO DE DISPARO</Label>
                <div className="grid grid-cols-2 gap-4">
                    <button
                        onClick={() => onIsScheduledChange(false)}
                        className={cn(
                            'p-6 border-2 rounded-xl transition-all text-center',
                            !isScheduled
                                ? 'border-[#7dd3fc] bg-[#7dd3fc]/5 shadow-md'
                                : 'border-gray-200 bg-white hover:border-gray-300'
                        )}
                    >
                        <Send className={cn(
                            'h-8 w-8 mx-auto mb-3',
                            !isScheduled ? 'text-[#7dd3fc]' : 'text-gray-400'
                        )} />
                        <h4 className="font-semibold text-gray-900">Enviar Agora</h4>
                        <p className="text-xs text-gray-600 mt-2">Envio imediato após confirmar</p>
                    </button>

                    <button
                        onClick={() => onIsScheduledChange(true)}
                        className={cn(
                            'p-6 border-2 rounded-xl transition-all text-center',
                            isScheduled
                                ? 'border-[#7dd3fc] bg-[#7dd3fc]/5 shadow-md'
                                : 'border-gray-200 bg-white hover:border-gray-300'
                        )}
                    >
                        <Calendar className={cn(
                            'h-8 w-8 mx-auto mb-3',
                            isScheduled ? 'text-[#7dd3fc]' : 'text-gray-400'
                        )} />
                        <h4 className="font-semibold text-gray-900">Agendar</h4>
                        <p className="text-xs text-gray-600 mt-2">Escolha data e hora</p>
                    </button>
                </div>

                {isScheduled && (
                    <div className="space-y-4 pt-4 border-t-2 border-gray-100">
                        <div className="space-y-2">
                            <Label className="text-sm font-medium flex items-center gap-2">
                                <Clock className="h-4 w-4 text-[#7dd3fc]" />
                                Data e Hora do Envio *
                            </Label>
                            <DateTimePicker
                                value={scheduledTime}
                                onChange={onScheduledTimeChange}
                                minDate={minDateTime}
                            />
                            <p className="text-xs text-gray-500">
                                O broadcast será enviado automaticamente na data e hora especificada.
                                O disparo será realizado considerando o fuso horário configurado em cada página.
                            </p>
                        </div>

                        {scheduledTime && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <div className="flex items-start gap-3">
                                    <Calendar className="h-5 w-5 text-blue-600 mt-0.5" />
                                    <div>
                                        <p className="text-sm font-medium text-blue-900">Agendado para:</p>
                                        <p className="text-sm text-blue-700 mt-1">
                                            {new Date(scheduledTime).toLocaleString('pt-BR', {
                                                dateStyle: 'full',
                                                timeStyle: 'short',
                                            })}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {!isScheduled && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                            <Send className="h-5 w-5 text-green-600 mt-0.5" />
                            <div>
                                <p className="text-sm font-medium text-green-900">Envio Imediato</p>
                                <p className="text-sm text-green-700 mt-1">
                                    Seu broadcast será enviado assim que você confirmar no próximo passo
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
